import axios from "axios";
import { createPortal } from "react-dom";
import { useState, useEffect } from "react";
import { Card, CardContent } from "ui/card";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { Badge } from "ui/badge";
import { Select, SelectContent, SelectItem, SelectValue } from "ui/select";
import { Switch } from "ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";
import {
  Search,
  Edit2,
  Trash2,
  Users,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import { useScreenPermission } from "hooks/useScreenPermission";
import Pagination from "ui/pagination";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";
import {
  getEmployeeTypes,
  EmployeeTypeApi,
  createEmployeeType,
  updateEmployeeType,
  deleteEmployeeType,
} from "services/employeeTypeService";

interface EmployeeType {
  id: string;
  code: string;
  employeeType: string;
  itsMember: "Yes" | "No";
  status: "active" | "inactive";
}

export const EmployeeTypesPage = () => {
  const { setPageHeader } = usePageHeader();
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<EmployeeType | null>(null);
  const [employeeTypes, setEmployeeTypes] = useState<EmployeeType[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState<EmployeeType | null>(null);

  const {
    canAdd,
    canEdit,
    canDelete,
    screenName,
    icon: ScreenIcon,
  } = useScreenPermission();

  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [formData, setFormData] = useState({
    employeeType: "",
    code: "",
    itsMember: "No" as "Yes" | "No",
    status: "active" as "active" | "inactive",
  });

  const [editFormData, setEditFormData] = useState({
    employeeType: "",
    code: "",
    itsMember: "No" as "Yes" | "No",
    status: "active" as "active" | "inactive",
  });

  const [errors, setErrors] = useState({
    code: "",
    employeeType: "",
  });

  const [editErrors, setEditErrors] = useState({
    code: "",
    employeeType: "",
  });

  const fetchEmployeeTypes = async () => {
    try {
      setLoading(true);
      const search = searchTerm.trim();

      const response = await getEmployeeTypes({
        typeName: search && !/^[A-Z0-9]{1,10}$/.test(search)
          ? search
          : undefined,
        code: search && /^[A-Z0-9]{1,10}$/.test(search)
          ? search.toUpperCase()
          : undefined,
        pageNo,
        pageSize,
      });
      const mappedData: EmployeeType[] = response.data.data.map(
        (item: EmployeeTypeApi) => ({
          id: item.publicId,
          employeeType: item.typeName,
          code: item.code,
          itsMember: item.isITSMember ? "Yes" : "No",
          status: item.isActive ? "active" : "inactive",
        }),
      );
      setEmployeeTypes(mappedData);
      setTotalCount(response.data.totalRecords);
    } catch (error) {
      console.error("Failed to load employee types:", error);
      toast.error("Failed to load employee types");
      setEmployeeTypes([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPageHeader({
      title: screenName,
      icon: <ScreenIcon className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
    fetchEmployeeTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, pageSize, searchTerm]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortColumn("");
        setSortDirection("asc");
      }
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedData = [...employeeTypes].sort((a, b) => {
    if (!sortColumn) return 0;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let aValue: any = a[sortColumn as keyof EmployeeType];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let bValue: any = b[sortColumn as keyof EmployeeType];
    if (typeof aValue === "string") {
      aValue = aValue.toLowerCase();
    }
    if (typeof bValue === "string") {
      bValue = bValue.toLowerCase();
    }
    if (aValue < bValue) {
      return sortDirection === "asc" ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortDirection === "asc" ? 1 : -1;
    }
    return 0;
  });

  const EMPLOYEE_CODE_REGEX = /^[A-Z]{1,10}$/;
  const EMPLOYEE_TYPE_REGEX = /^[A-Za-z ]{1,30}$/;

  const resetForm = () => {
    setFormData({
      employeeType: "",
      code: "",
      itsMember: "Yes",
      status: "active",
    });

    setErrors({
      code: "",
      employeeType: "",
    });
  };

  const resetEditForm = () => {
    setEditFormData({
      employeeType: "",
      code: "",
      itsMember: "Yes",
      status: "active",
    });

    setEditErrors({
      code: "",
      employeeType: "",
    });
  };

  const handleAdd = async () => {
  const newErrors = {
    code: "",
    employeeType: "",
  };

  if (!formData.code.trim()) {
    newErrors.code = "Employee Code is required";
  } else if (!EMPLOYEE_CODE_REGEX.test(formData.code)) {
    newErrors.code =
      "Employee Code can contain only letters (maximum 10 characters).";
  }

  if (!formData.employeeType.trim()) {
    newErrors.employeeType = "Employee Type is required";
  } else if (!EMPLOYEE_TYPE_REGEX.test(formData.employeeType.trim())) {
    newErrors.employeeType =
      "Employee Type can contain only letters and spaces (maximum 30 characters).";
  }

  const codeExists = employeeTypes.some(
    (item) => item.code.toLowerCase() === formData.code.toLowerCase(),
  );

  if (codeExists) {
    newErrors.code =
      "Employee Code already used for another employee type.";
  }

  setErrors(newErrors);

  if (newErrors.code || newErrors.employeeType) {
    return;
  }

  try {
    setLoading(true);

    const payload = {
      typeName: formData.employeeType.trim(),
      code: formData.code.toUpperCase(),
      isITSMember: formData.itsMember === "Yes",
      isActive: formData.status === "active",
    };

    const response = await createEmployeeType(payload);

    toast.success(
      response.message || "Employee type created successfully",
    );

    setIsAddDialogOpen(false);
    resetForm();

    await fetchEmployeeTypes();
  } catch (error) {
    toast.error(
      axios.isAxiosError(error)
        ? error.response?.data?.message ||
            "Failed to create employee type. Please try again."
        : error instanceof Error
          ? error.message
          : "Failed to create employee type.",
    );
  } finally {
    setSaving(false);
  }
};

  const handleEdit = (item: EmployeeType) => {
    setEditingItem(item);
    setEditFormData({
      employeeType: item.employeeType,
      code: item.code,
      itsMember: item.itsMember,
      status: item.status,
    });
    setEditErrors({
      code: "",
      employeeType: "",
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingItem) return;

    const newErrors = {
      code: "",
      employeeType: "",
    };

    if (!editFormData.code.trim()) {
      newErrors.code = "Employee Code is required";
    } else if (!EMPLOYEE_CODE_REGEX.test(editFormData.code)) {
      newErrors.code =
        "Employee Code can contain only letters and numbers (maximum 10 characters).";
    }

    if (!editFormData.employeeType.trim()) {
      newErrors.employeeType = "Employee Type is required";
    } else if (!EMPLOYEE_TYPE_REGEX.test(editFormData.employeeType.trim())) {
      newErrors.employeeType =
        "Employee Type can contain only letters, numbers and spaces (maximum 30 characters).";
    }

    const codeExists = employeeTypes.some(
      (item) =>
        item.code.toLowerCase() === editFormData.code.toLowerCase() &&
        item.id !== editingItem.id,
    );

    if (codeExists) {
      newErrors.code =
        "Employee Code already used for another employee type.";
    }

    setEditErrors(newErrors);

    if (newErrors.code || newErrors.employeeType) {
      return;
    }

    try {
      setSaving(true);

      const payload = {
        typeName: editFormData.employeeType.trim(),
        code: editFormData.code.toUpperCase(),
        isITSMember: editFormData.itsMember === "Yes",
        isActive: editFormData.status === "active",
      };

      const response = await updateEmployeeType(
        editingItem.id,
        payload,
      );

      toast.success(
        response.message || "Employee type updated successfully",
      );

      setIsEditDialogOpen(false);
      setEditingItem(null);
      resetEditForm();

      await fetchEmployeeTypes();
    } catch (error) {
      toast.error(
        axios.isAxiosError(error)
          ? error.response?.data?.message ||
              "Failed to update employee type. Please try again."
          : error instanceof Error
            ? error.message
            : "Failed to update employee type.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (item: EmployeeType) => {
    setDeletingItem(item);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingItem) return;
    try {
      setDeleting(true);
      const response = await deleteEmployeeType(deletingItem.id);
      toast.success(
        response.message || "Employee type deleted successfully",
      );
      setIsDeleteDialogOpen(false);
      setDeletingItem(null);
      await fetchEmployeeTypes();
    } catch (error) {
      toast.error(
        axios.isAxiosError(error)
          ? error.response?.data?.message ||
              "Failed to delete employee type. Please try again."
          : error instanceof Error
            ? error.message
            : "Failed to delete employee type.",
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      {(loading || saving || deleting) &&
        createPortal(
          <div className="fixed inset-0 z-[100]">
            <FullScreenLoading
              title={
                deleting
                  ? "Deleting employee type..."
                  : saving
                    ? "Saving employee type..."
                    : "Loading employee types..."
              }
            />
          </div>,
          document.body,
      )}
      <div className="space-y-6 pb-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center w-full gap-2">
          <div className="relative flex-1 max-w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search by employee code / type"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPageNo(1);
              }}
              className="pl-10"
            />
          </div>

          <div className="flex gap-2 shrink-0">
            {canAdd && (
              <Button
                onClick={() => {
                  resetForm();
                  setIsAddDialogOpen(true);
                }}
                style={{
                  background: "#5B9BD5",
                }}
                className="text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Employee Type
              </Button>
            )}
          </div>
        </div>

        {/* Table */}
        <Card
          className="bg-white border-[#5B9BD5]/30"
          style={{
            marginTop: "10px",
            gap: "0px",
          }}
        >
          <CardContent
            style={{
              padding: "0px 0px 10px 0px",
            }}
          >
            <div className="hidden lg:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow
                    style={{
                      backgroundColor: "#E3F2FD",
                    }}
                  >
                    <TableHead
                      style={{
                        color: "#5B9BD5",
                      }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("employeeType")}
                    >
                      <div className="flex items-center gap-1">
                        Employee Type
                        {sortColumn === "employeeType" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{
                        color: "#5B9BD5",
                      }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("code")}
                    >
                      <div className="flex items-center gap-1">
                        Code
                        {sortColumn === "code" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{
                        color: "#5B9BD5",
                      }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("itsMember")}
                    >
                      <div className="flex items-center gap-1">
                        ITS Member
                        {sortColumn === "itsMember" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{
                        color: "#5B9BD5",
                      }}
                      className="px-4 py-3 text-xs text-center"
                    >
                      Status
                    </TableHead>
                    {(canEdit || canDelete) && (
                      <TableHead
                        style={{
                          color: "#5B9BD5",
                        }}
                        className="px-4 py-3 text-xs text-right"
                      >
                        Actions
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {sortedData.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={canEdit || canDelete ? 5 : 4}
                        className="text-center py-8 px-4 text-gray-500 text-sm"
                      >
                        No employee types found
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedData.map((item) => (
                      <TableRow key={item.id} className="hover:bg-sky-50/30">
                        <TableCell className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">
                              {item.employeeType}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-2">
                          <Badge
                            variant="outline"
                            className="bg-sky-50 border-[#5B9BD5]/30 text-[#5B9BD5] font-mono text-xs"
                          >
                            {item.code}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-4 py-2">
                          <span className="text-sm text-gray-600">
                            {item.itsMember}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-2 text-center">
                          <Badge
                            variant="outline"
                            className={`${
                              item.status === "active"
                                ? "bg-green-100 text-green-700 border-green-200"
                                : "bg-gray-100 text-gray-700 border-gray-200"
                            } text-xs`}
                          >
                            {item.status === "active" ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        {(canEdit || canDelete) && (
                          <TableCell className="px-4 py-2 text-right">
                            <div className="flex items-center justify-end gap-1">
                              {canEdit && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleEdit(item)}
                                  className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                                  title="Edit"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </Button>
                              )}

                              {canDelete && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDelete(item)}
                                  className="text-red-500 hover:bg-red-50 h-7 w-7 p-0"
                                  title="Delete"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-3 p-4">
              {sortedData.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No employee types found</p>
                </div>
              ) : (
                sortedData.map((item) => (
                  <Card
                    key={item.id}
                    className="border-[#5B9BD5]/20 hover:border-[#5B9BD5]/40 transition-colors"
                  >
                    <CardContent
                      style={{
                        padding: "14px",
                      }}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div>
                            <p className="font-semibold text-gray-900">
                              {item.employeeType}
                            </p>

                            <Badge
                              variant="outline"
                              className="bg-sky-50 border-[#5B9BD5]/30 text-[#5B9BD5] font-mono text-xs mt-1"
                            >
                              {item.code}
                            </Badge>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2 mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">
                            ITS Member:
                          </span>

                          <span className="text-sm text-gray-700">
                            {item.itsMember}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between border-t border-gray-100">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Status:</span>

                          <Badge
                            variant="outline"
                            className={`${
                              item.status === "active"
                                ? "bg-green-100 text-green-700 border-green-200"
                                : "bg-gray-100 text-gray-700 border-gray-200"
                            } text-xs`}
                          >
                            {item.status === "active" ? "Active" : "Inactive"}
                          </Badge>
                        </div>

                        <div className="flex items-center gap-2">
                          {canEdit && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(item)}
                              className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                            >
                              <Edit2 className="w-3 h-3 mr-1" />
                            </Button>
                          )}

                          {canDelete && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(item)}
                              className="h-8 text-xs text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </CardContent>

          <Pagination
            totalItems={totalCount}
            itemsPerPage={pageSize}
            currentPage={pageNo}
            onPageChange={(page) => setPageNo(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPageNo(1);
            }}
            label="employee types"
          />
        </Card>

        {/* Add Dialog */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent className="sm:max-w-[600px] bg-white">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>
                Add Employee Type
              </DialogTitle>

              <DialogDescription>
                Create a new employee type entry
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Employee Type */}
                <div className="space-y-2">
                  <InputFieldset
                    label="Employee Type"
                    required
                    disabled={saving}
                    id="employeeType"
                    value={formData.employeeType}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/[^A-Za-z ]/g, "")
                        .slice(0, 30);

                      setFormData({
                        ...formData,
                        employeeType: value,
                      });

                      setErrors({
                        ...errors,
                        employeeType: "",
                      });
                    }}
                    placeholder="e.g., Staff"
                    className={`text-sm ${
                      errors.employeeType
                        ? "border-red-500 focus:ring-red-500"
                        : ""
                    }`}
                  />

                  {errors.employeeType && (
                    <p className="text-red-500 text-xs">
                      {errors.employeeType}
                    </p>
                  )}
                </div>

                {/* Employee Code */}
                <div className="space-y-2">
                  <InputFieldset
                    label="Employee Code"
                    required
                    disabled={saving}
                    id="code"
                    value={formData.code}
                    onChange={(e) => {
                      const value = e.target.value
                        .toUpperCase()
                        .replace(/[^A-Z]/g, "")
                        .slice(0, 10);

                      setFormData({
                        ...formData,
                        code: value,
                      });

                      setErrors({
                        ...errors,
                        code: "",
                      });
                    }}
                    placeholder="e.g., KG, STF"
                    className={`text-sm ${
                      errors.code ? "border-red-500 focus:ring-red-500" : ""
                    }`}
                  />

                  {errors.code && (
                    <p className="text-red-500 text-xs">{errors.code}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* ITS Member */}
                <div className="space-y-2">
                  <SelectFieldset label="ITS Member">
                    <Select
                      value={formData.itsMember}
                      disabled={saving}
                      onValueChange={(value: "Yes" | "No" ) =>
                        setFormData({
                          ...formData,
                          itsMember: value,
                        })
                      }
                    >
                      <SelectTriggerUI className="h-9 text-sm">
                        <SelectValue />
                      </SelectTriggerUI>
                      <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </SelectFieldset>
                </div>
                {/* Status */}
                <div className="min-w-0 flex flex-col justify-center gap-2 px-3 py-2">
                  <label
                    htmlFor="employee-type-status"
                    className="text-sm font-medium text-gray-700"
                  >
                    Status
                  </label>
                  <div className="flex items-center gap-3">
                    <Switch
                      id="employee-type-status"
                      disabled={saving}
                      checked={formData.status === "active"}
                      onCheckedChange={(checked) =>
                        setFormData((current) => ({
                          ...current,
                          status: checked ? "active" : "inactive",
                        }))
                      }
                    />
                    <span className="text-sm text-gray-700">
                      {formData.status === "active" ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                disabled={saving}
                onClick={() => {
                  setIsAddDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>

              <Button
                onClick={handleAdd}
                disabled={saving}
                style={{
                  background: "#5B9BD5",
                }}
                className="text-white"
              >
                Add Employee Type
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="sm:max-w-[600px] bg-white">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>
                Edit Employee Type
              </DialogTitle>

              <DialogDescription>
                Update employee type information
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Employee Type */}
                <div className="space-y-2">
                  <InputFieldset
                    label="Employee Type"
                    required
                    id="edit-employeeType"
                    value={editFormData.employeeType}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/[^A-Za-z ]/g, "")
                        .slice(0, 30);

                      setEditFormData({
                        ...editFormData,
                        employeeType: value,
                      });

                      setEditErrors({
                        ...editErrors,
                        employeeType: "",
                      });
                    }}
                    placeholder="e.g., Staff"
                    className={`text-sm ${
                      editErrors.employeeType
                        ? "border-red-500 focus:ring-red-500"
                        : ""
                    }`}
                  />

                  {editErrors.employeeType && (
                    <p className="text-red-500 text-xs">
                      {editErrors.employeeType}
                    </p>
                  )}
                </div>
                {/* Employee Code */}
                <div className="space-y-2">
                  <InputFieldset
                    label="Employee Code"
                    required
                    id="edit-code"
                    value={editFormData.code}
                    onChange={(e) => {
                      const value = e.target.value
                        .toUpperCase()
                        .replace(/[^A-Z0-9]/g, "")
                        .slice(0, 10);

                      setEditFormData({
                        ...editFormData,
                        code: value,
                      });

                      setEditErrors({
                        ...editErrors,
                        code: "",
                      });
                    }}
                    placeholder="e.g., KG, STF"
                    className={`text-sm ${
                      editErrors.code ? "border-red-500 focus:ring-red-500" : ""
                    }`}
                  />

                  {editErrors.code && (
                    <p className="text-red-500 text-xs">{editErrors.code}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* ITS Member */}
                <div className="space-y-2">
                  <SelectFieldset label="ITS Member">
                    <Select
                      value={editFormData.itsMember}
                      onValueChange={(value: "Yes" | "No" ) =>
                        setEditFormData({
                          ...editFormData,
                          itsMember: value,
                        })
                      }
                    >
                      <SelectTriggerUI className="h-9 text-sm">
                        <SelectValue />
                      </SelectTriggerUI>

                      <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </SelectFieldset>
                </div>
                {/* Status */}
                <div className="min-w-0 flex flex-col justify-center gap-2 px-3 py-2">
                  <label
                    htmlFor="edit-employee-type-status"
                    className="text-sm font-medium text-gray-700"
                  >
                    Status
                  </label>

                  <div className="flex items-center gap-3">
                    <Switch
                      id="edit-employee-type-status"
                      disabled={saving}
                      checked={editFormData.status === "active"}
                      onCheckedChange={(checked) =>
                        setEditFormData((current) => ({
                          ...current,
                          status: checked ? "active" : "inactive",
                        }))
                      }
                    />

                    <span className="text-sm text-gray-700">
                      {editFormData.status === "active" ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                disabled={saving}
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setEditingItem(null);
                  resetEditForm();
                }}
              >
                Cancel
              </Button>

              <Button
                onClick={handleUpdate}
                disabled={saving}
                style={{
                  background: "#5B9BD5",
                }}
                className="text-white"
              >
                Update Employee Type
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-red-600">
                Delete Employee Type
              </DialogTitle>

              <DialogDescription>
                Are you sure you want to delete{" "}
                <span className="font-semibold">
                  "{deletingItem?.employeeType}"
                </span>
                ? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>

            <DialogFooter>
              <Button
                variant="outline"
                disabled={deleting}
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setDeletingItem(null);
                }}
              >
                No, Cancel
              </Button>

              <Button
                onClick={confirmDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Yes, Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};
